import { CommandBarButton, mergeStyles, OverflowSet, useTheme } from '@fluentui/react';
import { ChevronRightIcon } from '@fluentui/react-icons-mdl2';
import { RootState } from 'app/store';
import { fadeIn } from 'global/Application';
import { updateCollapse } from 'global/Reducer/statusSlice';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ICollapsiblePanelProperties } from './CustomPanel.types';

// ============================================================================
// Constants
// ============================================================================

const VISIBILITY_STYLES = {
    visible: { opacity: '1', transition: 'opacity 1.4s, max-height 0.1s ease-out', maxHeight: 'none' },
    hidden: { opacity: '0', transition: 'opacity 0s, max-height 0.1s ease-out', maxHeight: '0', display: 'none' },
} as const;

const LAYOUT_CONSTANTS = {
    headerMinHeight: '45px',
    contentPadding: '10px',
    contentTransition: 'max-height 0.1s ease-out',
    containerBorder: '2px solid rgba(180,180,180,0.1)',
    containerMaxWidth: '100%',
} as const;

const SPACING = {
    headerMarginCollapsible: '5px',
    headerMarginDefault: '15px',
    chevronMargin: '15px',
    overflowMarginRight: '15px',
    commandBarPadding: '10px',
    overflowButtonPadding: '0px 4px',
} as const;

const ANIMATION = {
    chevronRotation: 'rotate(90deg)',
    chevronTransition: '180ms ease all',
    collapsedFontWeight: 300,
    expandedFontWeight: 450,
} as const;

const KEYBOARD_CODES = {
    space: 'Space',
    enter: 'Enter',
    arrowRight: 'ArrowRight',
} as const;

const ALLOWED_KEYS = [
    KEYBOARD_CODES.space,
    KEYBOARD_CODES.enter,
    KEYBOARD_CODES.arrowRight,
];

const MENUITEM_STYLES = {
    root: {
        padding: SPACING.commandBarPadding,
        margin: '0px',
    },
}

const OVERFLOW_BUTTON_STYLES = {
    root: {
        minWidth: 0,
        padding: SPACING.overflowButtonPadding,
        alignSelf: 'stretch',
        backgroundColor: 'transparent',
    },
}

const ROUTE_EXCLUSION = '/records/blueRelationship/';

// ============================================================================
// Component
// ============================================================================

export default function CustomPanel(props: ICollapsiblePanelProperties) {
    const {
        title,
        children,
        onCollapseChange,
        contentStyle,
        canCollapse,
        style,
        headerStyle,
        id,
        storageId,
        defaultCollapsed,
        collapsed: controlledCollapsed,
        disabled,
        mountWhenExpanded,
        items,
        overflowItems,
        onRenderHeaderTitle,
        onRenderHeaderContent,
    } = props;

    const theme = useTheme();
    const dispatch = useDispatch();
    const store = useSelector((state) => state) as RootState;

    // ========================================================================
    // State Management
    // ========================================================================

    const [localCollapsed, setLocalCollapsed] = useState<boolean>(() =>
        getInitialCollapsedState(storageId, store, defaultCollapsed)
    );

    // Use controlled prop if provided, otherwise use local state
    const isCollapsed = controlledCollapsed !== undefined ? controlledCollapsed : localCollapsed;

    // ========================================================================
    // Effects
    // ========================================================================

    /**
     * Reset to default collapsed state when defaultCollapsed prop changes,
     * but only for routes outside of the blue relationship section.
     */
    useEffect(() => {
        const isBlueRelationshipRoute = store.router.location.pathname.startsWith(
            ROUTE_EXCLUSION
        );

        if (!isBlueRelationshipRoute && defaultCollapsed !== undefined) {
            setLocalCollapsed(defaultCollapsed);
        }
    }, [defaultCollapsed, store.router.location.pathname]);

    // ========================================================================
    // Memoized Styles
    // ========================================================================

    const headerStyles = useMemo(
        () =>
            mergeStyles({
                backgroundColor: theme.semanticColors.menuBackground,
                color: theme.semanticColors.bodyText,
                fontSize: theme.fonts.mediumPlus.fontSize,
                cursor: canCollapse ? 'pointer' : 'unset',
                display: 'flex',
                alignItems: 'center',
                minHeight: LAYOUT_CONSTANTS.headerMinHeight,
                ':hover': {
                    backgroundColor: canCollapse
                        ? theme.semanticColors.menuItemBackgroundHovered
                        : 'unset',
                },
            }),
        [theme, canCollapse]
    );

    const contentContainerStyles = useMemo(
        () => ({
            padding: LAYOUT_CONSTANTS.contentPadding,
            overflow: isCollapsed ? 'hidden' : 'auto',
            maxWidth: '100%',
            minWidth: 0,
            ...contentStyle,
            ...(isCollapsed ? VISIBILITY_STYLES.hidden : VISIBILITY_STYLES.visible),
        }),
        [isCollapsed, contentStyle]
    );

    const containerStyles = useMemo(
        () => ({
            width: '100%',
            minWidth: 0,
            maxWidth: LAYOUT_CONSTANTS.containerMaxWidth,
            boxSizing: 'border-box' as const,
            ...style,
            border: LAYOUT_CONSTANTS.containerBorder,
        }),
        [style]
    );

    // ========================================================================
    // Handlers
    // ========================================================================
    const handleHeaderClick = useCallback(() => {
        if (!canCollapse) return;

        const newCollapsedState = !isCollapsed;

        if (onCollapseChange) {
            onCollapseChange(newCollapsedState);
        }

        setLocalCollapsed(newCollapsedState);

        if (storageId) {
            dispatch(updateCollapse({ id: storageId, collapse: newCollapsedState }));
        }
    }, [canCollapse, isCollapsed, onCollapseChange, storageId, dispatch]);

    const handleHeaderKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (disabled) return;

            const code = event.code as typeof KEYBOARD_CODES[keyof typeof KEYBOARD_CODES];


            if (ALLOWED_KEYS.includes(code)) {
                event.preventDefault();
                handleHeaderClick();
            }
        },
        [disabled, handleHeaderClick]
    );

    // ========================================================================
    // Render
    // ========================================================================
    return (
        <div
            key={id?.toLowerCase()}
            id={id?.toLowerCase()}
            style={containerStyles}
        >
            <div
                className={headerStyles}
                data-collapsed={isCollapsed}
                onClick={handleHeaderClick}
                onKeyDown={handleHeaderKeyDown}
                tabIndex={0}
                role="button"
                aria-expanded={!isCollapsed}
            >
                {onRenderHeaderContent
                    ? onRenderHeaderContent({} as ICollapsiblePanelProperties)
                    : (
                        <>
                            {canCollapse && (
                                <div
                                    style={{
                                        marginLeft: SPACING.chevronMargin,
                                        transition: ANIMATION.chevronTransition,
                                        transform: !isCollapsed ? ANIMATION.chevronRotation : '',
                                    }}
                                >
                                    <ChevronRightIcon style={{ fontSize: 'smaller' }} />
                                </div>
                            )}

                            {onRenderHeaderTitle
                                ? onRenderHeaderTitle(props)
                                : (
                                    <div
                                        style={{
                                            marginLeft: canCollapse
                                                ? SPACING.headerMarginCollapsible
                                                : SPACING.headerMarginDefault,
                                            fontWeight: isCollapsed ? ANIMATION.collapsedFontWeight : ANIMATION.expandedFontWeight,
                                            ...headerStyle,
                                        }}
                                    >
                                        {title}
                                    </div>
                                )}

                            {(items || overflowItems) && (
                                <div
                                    key="overflow"
                                    style={{
                                        marginLeft: 'auto',
                                        marginRight: SPACING.overflowMarginRight,
                                    }}
                                >
                                    <OverflowSet
                                        aria-label="Panel actions"
                                        role="menubar"
                                        items={items ?? []}
                                        overflowItems={overflowItems ?? []}
                                        onRenderItem={(item): JSX.Element => {
                                            if (item.onRender) {
                                                return item.onRender(item);
                                            }

                                            return (
                                                <CommandBarButton
                                                    role="menuitem"
                                                    iconProps={{ iconName: item.icon }}
                                                    styles={MENUITEM_STYLES}
                                                    menuProps={item.subMenuProps}
                                                    text={item.name}
                                                    onClick={item.onClick}
                                                />
                                            );
                                        }}
                                        onRenderOverflowButton={(renderProps) => (
                                            <CommandBarButton
                                                ariaLabel="More items"
                                                role="menuitem"
                                                styles={OVERFLOW_BUTTON_STYLES}
                                                menuIconProps={{ iconName: 'More' }}
                                                menuProps={{ items: renderProps! }}
                                            />
                                        )}
                                    />
                                </div>
                            )}
                        </>
                    )}
            </div>
            <div
                className={fadeIn}
                style={contentContainerStyles}
            >
                {(!mountWhenExpanded || !isCollapsed) && (
                    children
                )}
            </div>
        </div>
    );
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Determines the initial collapsed state based on storage, defaults, or local state.
 */
function getInitialCollapsedState(
    storageId: string | undefined,
    store: RootState,
    defaultCollapsed: boolean | undefined
): boolean {
    if (
        storageId &&
        store.status?.panelStatus[storageId] !== undefined
    ) {
        return store.status.panelStatus[storageId];
    }
    return defaultCollapsed ?? false;
}

